import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import html2pdf from 'html2pdf.js';
import { buildFunctionUrl } from '../config/functions';
import accessoryCatalog from '../config/accessories';
import windowPricingTables, { getWindowBasePrice } from '../config/windowPricing';
import {
  productCategories as defaultProductCategories,
  designPresets as defaultDesignPresets,
  colorOptions as defaultColorOptions,
  glassOptions as defaultGlassOptions,
  finishOptions as defaultFinishOptions,
  getCategoryTitle as getDefaultCategoryTitle,
} from '../config/productCatalog';

const formatSeriesLabel = (series) => (series ? `Lama ${series.replace('lama', '')}` : '');

const windowSeriesOrder = ['lama3', 'lama4', 'lama6'];
const windowSeriesOptions = windowSeriesOrder
  .filter((key) => windowPricingTables[key])
  .map((key) => ({
    id: key,
    label: formatSeriesLabel(key),
  }));

const certificationOptions = ['Miami-Dade', 'Florida NOA', 'Energy Star', 'Impact-Rated', 'Acústico'];

const measurementTips = [
  { title: '1. Usa cinta metálica', desc: 'Mide dentro del marco y en pulgadas decimales.' },
  { title: '2. Alto × Ancho', desc: 'Ingresa primero el alto y luego el ancho (ej. 72" x 48").' },
  { title: '3. Cantidades', desc: 'Indica cuántas unidades por diseño para tu proyecto.' },
];

const resolveCategoryTitle = (id, categories = defaultProductCategories) =>
  categories.find((cat) => cat.id === id)?.title || getDefaultCategoryTitle(id) || id;

const buildInitialFormState = (defaultDesign, defaultCategoryId) => ({
  fullName: '',
  email: '',
  phone: '',
  projectLocation: '',
  projectType: 'residencial',
  productCategory: defaultCategoryId || 'ventanas',
  designStyle: defaultDesign.id,
  designLabel: defaultDesign.title,
  designDesc: defaultDesign.desc,
  designImage: defaultDesign.image,
  width: '',
  height: '',
  quantity: '1',
  color: 'blanco',
  glass: 'clear',
  finish: 'mate',
  certifications: [],
  notes: '',
  accessoryQuantities: {},
  windowSeries: 'lama3',
});

const BusinessInfoForm = ({
  selectedModels: initialSelectedModels = [],
  onUpdateSelectedModels = () => {},
  catalog = {},
}) => {
  const catalogProductCategories = Array.isArray(catalog.productCategories)
    ? catalog.productCategories
    : defaultProductCategories;
  const catalogDesignPresets = catalog.designPresets || defaultDesignPresets;
  const catalogColorOptions = Array.isArray(catalog.colorOptions) ? catalog.colorOptions : defaultColorOptions;
  const catalogGlassOptions = Array.isArray(catalog.glassOptions) ? catalog.glassOptions : defaultGlassOptions;
  const catalogFinishOptions = Array.isArray(catalog.finishOptions) ? catalog.finishOptions : defaultFinishOptions;

  const defaultCategoryId = catalogProductCategories[0]?.id || 'ventanas';
  const defaultDesign =
    catalogDesignPresets[defaultCategoryId]?.[0] ||
    catalogDesignPresets.ventanas?.[0] || {
      id: 'casement',
      title: 'Casement',
      desc: '',
      image: '',
    };

  const initialFormState = useMemo(
    () => buildInitialFormState(defaultDesign, defaultCategoryId),
    [defaultDesign, defaultCategoryId]
  );
  const [formData, setFormData] = useState(() => initialFormState);
  const [formValid, setFormValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [models, setModels] = useState(initialSelectedModels);

  const getCategoryTitleSafe = (id) => resolveCategoryTitle(id, catalogProductCategories);

  const currentDesigns = useMemo(
    () => catalogDesignPresets[formData.productCategory] || [],
    [catalogDesignPresets, formData.productCategory]
  );

  const selectedDesign = useMemo(() => {
    const match = currentDesigns.find((design) => design.id === formData.designStyle);
    return {
      id: formData.designStyle,
      title: match?.title || formData.designLabel || 'Modelo seleccionado',
      desc: match?.desc || formData.designDesc || '',
      image: match?.image || formData.designImage || '',
    };
  }, [
    currentDesigns,
    formData.designStyle,
    formData.designLabel,
    formData.designDesc,
    formData.designImage,
  ]);

  const manualModel = useMemo(() => {
    if (!formData.designStyle) return null;
    return {
      id: `manual-${formData.designStyle}`,
      designId: formData.designStyle,
      name: formData.designLabel,
      description: formData.designDesc,
      image: formData.designImage,
      categoryId: formData.productCategory,
      categoryName: getCategoryTitleSafe(formData.productCategory),
      height: formData.height,
      width: formData.width,
      quantity: formData.quantity,
      series: formData.productCategory === 'ventanas' ? formData.windowSeries : undefined,
    };
  }, [
    formData.designStyle,
    formData.designDesc,
    formData.designImage,
    formData.designLabel,
    formData.productCategory,
    formData.quantity,
    formData.height,
    formData.width,
    formData.windowSeries,
  ]);

  const filteredAccessories = useMemo(
    () =>
      accessoryCatalog.filter(
        (item) => !item.appliesTo || item.appliesTo.includes(formData.productCategory)
      ),
    [formData.productCategory]
  );

  const selectedAccessories = useMemo(
    () =>
      accessoryCatalog
        .map((item) => ({
          ...item,
          quantity: Number(formData.accessoryQuantities[item.id]) || 0,
        }))
        .filter((item) => item.quantity > 0),
    [formData.accessoryQuantities]
  );

  const accessoriesTotal = useMemo(
    () => selectedAccessories.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [selectedAccessories]
  );

  const windowEstimate = useMemo(() => {
    if (formData.productCategory !== 'ventanas') return null;
    const width = Number(formData.width);
    const height = Number(formData.height);
    if (!width || !height) return null;
    return getWindowBasePrice(width, height, formData.windowSeries);
  }, [formData.productCategory, formData.width, formData.height, formData.windowSeries]);

  useEffect(() => {
    if (currentDesigns.length && !currentDesigns.find((d) => d.id === formData.designStyle)) {
      setFormData((prev) => ({
        ...prev,
        designStyle: currentDesigns[0].id,
        designLabel: currentDesigns[0].title,
        designDesc: currentDesigns[0].desc,
        designImage: currentDesigns[0].image,
      }));
    } else if (currentDesigns.length) {
      const match = currentDesigns.find((d) => d.id === formData.designStyle);
      if (match && match.title !== formData.designLabel) {
        setFormData((prev) => ({
          ...prev,
          designLabel: match.title,
          designDesc: match.desc,
          designImage: match.image,
        }));
      }
    }
  }, [currentDesigns, formData.designStyle, formData.designLabel]);

  useEffect(() => {
    setFormData((prev) => {
      const filtered = Object.entries(prev.accessoryQuantities || {}).filter(([id]) => {
        const item = accessoryCatalog.find((acc) => acc.id === id);
        return !item?.appliesTo || item.appliesTo.includes(formData.productCategory);
      });
      if (filtered.length === Object.keys(prev.accessoryQuantities || {}).length) {
        return prev;
      }
      return { ...prev, accessoryQuantities: Object.fromEntries(filtered) };
    });
  }, [formData.productCategory]);

  useEffect(() => {
    setModels(initialSelectedModels);
  }, [initialSelectedModels]);

  useEffect(() => {
    const contactValid =
      formData.fullName && formData.email && formData.phone && formData.projectLocation;
    const measurementsValid =
      models.length > 0 ||
      (Number(formData.width) > 0 && Number(formData.height) > 0 && Number(formData.quantity) > 0);
    setFormValid(Boolean(contactValid && measurementsValid));
  }, [formData, models.length]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProductCategory = (categoryId) => {
    setFormData((prev) => ({ ...prev, productCategory: categoryId }));
  };

  const handleDesignSelect = (designId) => {
    const option = currentDesigns.find((design) => design.id === designId);
    setFormData((prev) => ({
      ...prev,
      designStyle: designId,
      designLabel: option?.title || prev.designLabel,
      designDesc: option?.desc || prev.designDesc,
      designImage: option?.image || prev.designImage,
    }));
  };

  const syncModels = (nextModels) => {
    setModels(nextModels);
    onUpdateSelectedModels(nextModels);
  };

  const handleModelFieldChange = (index, field, value) => {
    setModels((prev) => {
      const next = prev.map((model, idx) => (idx === index ? { ...model, [field]: value } : model));
      onUpdateSelectedModels(next);
      return next;
    });
  };

  const handleModelRemove = (index) => {
    setModels((prev) => {
      const next = prev.filter((_, idx) => idx !== index);
      onUpdateSelectedModels(next);
      return next;
    });
  };

  const toggleCertification = (value) => {
    setFormData((prev) => {
      const exists = prev.certifications.includes(value);
      return {
        ...prev,
        certifications: exists
          ? prev.certifications.filter((item) => item !== value)
          : [...prev.certifications, value],
      };
    });
  };

  const handleAccessoryQuantityChange = (id, value) => {
    const quantity = Math.max(0, Number(value));
    setFormData((prev) => ({
      ...prev,
      accessoryQuantities: { ...(prev.accessoryQuantities || {}), [id]: quantity },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formValid) {
      setMessage('⚠️ Completa la información requerida.');
      return;
    }

    setSubmitting(true);
    setMessage('');

    const sessionId = sessionStorage.getItem('sessionId') || null;

    const safeFullName = (formData.fullName || '').trim();
    const safeEmail = (formData.email || '').trim();
    const safePhone = (formData.phone || '').trim();
    const safeLocation = (formData.projectLocation || '').trim();
    const submittedData = { ...formData, fullName: safeFullName, email: safeEmail, phone: safePhone, projectLocation: safeLocation };

    const baseModels = models.length ? models : manualModel ? [manualModel] : [];
    const normalizedModels = baseModels.map((model, idx) => {
      const rawCategoryId = model.categoryId || formData.productCategory;
      const categoryId = (rawCategoryId || '').toString().toLowerCase();
      const width = Number(model.width) || null;
      const height = Number(model.height) || null;
      const quantity = Number(model.quantity) || 1;
      const series =
        categoryId === 'ventanas'
          ? model.series || formData.windowSeries || 'lama3'
          : null;
      const estimate =
        categoryId === 'ventanas' && width && height
          ? getWindowBasePrice(width, height, series || 'lama3')
          : null;

      return {
        ...model,
        uid: model.id || `model-${idx}`,
        categoryId,
        categoryName: model.categoryName || getCategoryTitleSafe(categoryId),
        width,
        height,
        quantity,
        series,
        estimate,
      };
    });

    if (!normalizedModels.length) {
      normalizedModels.push({
        uid: 'manual-fallback',
        categoryId: formData.productCategory,
        categoryName: getCategoryTitleSafe(formData.productCategory),
        designId: formData.designStyle,
        name: formData.designLabel,
        description: formData.designDesc,
        image: formData.designImage,
        width: Number(formData.width) || null,
        height: Number(formData.height) || null,
        quantity: Number(formData.quantity) || 1,
        series: formData.productCategory === 'ventanas' ? formData.windowSeries : null,
        estimate: windowEstimate,
      });
    }

    const primaryModel = normalizedModels[0];

    const payloadMeta = {
      projectLocation: safeLocation,
      projectType: formData.projectType,
      productCategory: primaryModel?.categoryId || formData.productCategory,
      designStyle: primaryModel?.designId || formData.designStyle,
      windowSeries: primaryModel?.series || formData.windowSeries,
      dimensions: {
        width: (primaryModel?.width ?? Number(formData.width)) || null,
        height: (primaryModel?.height ?? Number(formData.height)) || null,
        quantity: (primaryModel?.quantity ?? Number(formData.quantity)) || 1,
      },
      finishes: {
        color: formData.color,
        glass: formData.glass,
        finish: formData.finish,
      },
      design: {
        id: primaryModel?.designId || formData.designStyle,
        label: primaryModel?.name || formData.designLabel,
        description: primaryModel?.description || formData.designDesc,
        image: primaryModel?.image || formData.designImage,
      },
      models: normalizedModels,
      certifications: formData.certifications,
      notes: formData.notes,
      windowEstimate: primaryModel?.estimate || windowEstimate,
      accessories: selectedAccessories.map((item) => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        unit: item.unit,
        price: item.price,
        subtotal: Number(item.price) * item.quantity,
      })),
      accessoriesTotal,
    };

    const cardInformation = normalizedModels.map((model) => {
      const dims = model.height && model.width ? `${model.height}" × ${model.width}"` : 'Medidas por confirmar';
      const seriesLabel = model.series ? formatSeriesLabel(model.series) : '';
      return `${model.categoryName || getCategoryTitleSafe(model.categoryId)} · ${model.name || 'Modelo'} · ${dims} · ${model.quantity || 1} uds${seriesLabel ? ` · ${seriesLabel}` : ''}`;
    });

    const businessLabelParts = [safeFullName || 'Lead PRWD'];
    if (safeLocation) businessLabelParts.push(safeLocation);
    const businessName = businessLabelParts.join(' · ');
    const businessType = [getCategoryTitleSafe(primaryModel?.categoryId || formData.productCategory), primaryModel?.name]
      .filter(Boolean)
      .join(' · ');

    const adminPayload = {
      businessName,
      businessType,
      email: safeEmail,
      phone: safePhone,
      hasDomain: formData.projectType,
      cardInformation,
      additionalInfo: formData.notes || 'Sin notas adicionales.',
      fullName: safeFullName,
      payload: payloadMeta,
    };

    try {
      let leadCreated = false;
      let leadData = null;

      const leadResp = await fetch(buildFunctionUrl('/registerLandingLead'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: safeFullName,
          email: safeEmail,
          phone: safePhone,
          source: 'landing_page',
          sessionId,
          status: 'new',
          cardInformation,
          meta: payloadMeta,
        }),
      });

      if (!leadResp.ok) {
        const text = await leadResp.text();
        throw new Error(`HTTP ${leadResp.status}: ${text}`);
      }
      leadData = await leadResp.json().catch(() => null);
      leadCreated = true;

      const adminResp = await fetch(buildFunctionUrl('/sendBusinessInfo'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(adminPayload),
      });

      if (!adminResp.ok) {
        const text = await adminResp.text();
        console.warn('⚠️ Error al enviar correo admin:', text);
        throw new Error(`Falló el correo admin: ${adminResp.status}`);
      }

      if (leadCreated && typeof window.gtag !== 'undefined') {
        window.gtag('event', 'conversion', {
          send_to: 'AW-16987830296/zaL6CJTh4pYYENz3rJUp',
          value: 1,
          currency: 'USD',
          event_label: `Landing Conversion ${leadData?.leadId || ''}`.trim(),
        });
      }

      setMessage('✅ ¡Gracias! Recibimos tu solicitud y un asesor te contactará muy pronto.');
      generatePreliminaryQuotePDF({
        formData: submittedData,
        models: normalizedModels,
        accessories: selectedAccessories,
        accessoriesTotal,
        leadId: leadData?.leadId,
        categoryOptions: catalogProductCategories,
      });
      setFormData({ ...initialFormState });
      setModels([]);
      onUpdateSelectedModels([]);
    } catch (error) {
      console.error('❌ Error en el envío del formulario:', error);
      setMessage('❌ Ocurrió un error al procesar la solicitud. Intenta nuevamente.');
    }

    setSubmitting(false);
  };

  return (
    <div className="relative bg-white p-6 sm:p-8 rounded-3xl shadow-2xl border" style={{ borderColor: '#d3e1d1' }}>
      <Helmet>
        <script async src="https://www.googletagmanager.com/gtag/js?id=AW-16987830296"></script>
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'AW-16987830296');
          `}
        </script>
      </Helmet>

      <div className="rounded-2xl bg-gradient-to-r from-emerald-600 to-emerald-800 text-white p-6 mb-6">
        <p className="text-xs uppercase tracking-[0.4em]">Cotización inteligente</p>
        <h2 className="text-2xl font-semibold mt-2">Cuéntanos sobre tu sistema de puertas y ventanas</h2>
        <p className="text-sm text-emerald-100 mt-1">
          Capturamos medidas, acabados y certificaciones para asignarte el asesor ideal.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <section className="space-y-4">
          <p className="text-xs uppercase tracking-[0.4em] text-gray-500">Datos de contacto</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-gray-700">Nombre completo</label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Nombre y apellido"
                required
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="tu@email.com"
                required
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700">Teléfono / WhatsApp</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="+1 787 000 0000"
                required
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700">Ubicación del proyecto</label>
              <input
                type="text"
                name="projectLocation"
                value={formData.projectLocation}
                onChange={handleInputChange}
                className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                placeholder="Ciudad o urbanización"
                required
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-gray-700">Uso del proyecto</label>
              <select
                name="projectType"
                value={formData.projectType}
                onChange={handleInputChange}
                className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="residencial">Residencial</option>
                <option value="comercial">Comercial</option>
                <option value="mixto">Mixto</option>
              </select>
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <div className="flex flex-col gap-2">
            <p className="text-xs uppercase tracking-[0.4em] text-gray-500">Modelos seleccionados</p>
            <p className="text-sm text-gray-600">
              {models.length > 0
                ? 'Edita las medidas de cada modelo agregado desde la galería.'
                : 'Selecciona un estilo en la galería o usa las opciones siguientes para elegir uno.'}
            </p>
          </div>
          {models.length > 0 ? (
            <div className="space-y-4">
              {models.map((model, index) => {
                const numericWidth = Number(model.width);
                const numericHeight = Number(model.height);
                const previewEstimate =
                  model.categoryId === 'ventanas' && numericWidth > 0 && numericHeight > 0
                    ? getWindowBasePrice(numericWidth, numericHeight, model.series || 'lama3')
                    : null;
                return (
                <article
                  key={model.id || `${model.designId}-${index}`}
                  className="rounded-3xl border border-emerald-100 bg-emerald-50/60 p-4 shadow-sm space-y-3"
                >
                  <div className="flex items-center gap-3">
                    {model.image ? (
                      <img src={model.image} alt={model.name} className="h-16 w-16 rounded-2xl object-cover" />
                    ) : (
                      <div className="h-16 w-16 rounded-2xl bg-white/60 flex items-center justify-center text-gray-500 text-xs">
                        Sin foto
                      </div>
                    )}
                    <div>
                      <p className="text-xs uppercase tracking-wide text-emerald-700">
                        {model.categoryName || model.categoryId || 'Modelo'}
                      </p>
                      <p className="text-lg font-semibold text-gray-900">{model.name}</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-3">
                    <label className="text-sm font-semibold text-gray-700">
                      Alto (pulgadas)
                      <input
                        type="number"
                        min="1"
                        value={model.height ?? ''}
                        onChange={(e) => handleModelFieldChange(index, 'height', e.target.value)}
                        className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="text-sm font-semibold text-gray-700">
                      Ancho (pulgadas)
                      <input
                        type="number"
                        min="1"
                        value={model.width ?? ''}
                        onChange={(e) => handleModelFieldChange(index, 'width', e.target.value)}
                        className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="text-sm font-semibold text-gray-700">
                      Cantidad
                      <input
                        type="number"
                        min="1"
                        value={model.quantity ?? '1'}
                        onChange={(e) => handleModelFieldChange(index, 'quantity', e.target.value)}
                        className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm"
                      />
                    </label>
                  </div>
                  {model.categoryId === 'ventanas' && (
                    <label className="text-sm font-semibold text-gray-700 block">
                      Serie (lama)
                      <select
                        value={model.series || 'lama3'}
                        onChange={(e) => handleModelFieldChange(index, 'series', e.target.value)}
                        className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm"
                      >
                        {windowSeriesOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {previewEstimate?.widthExceeded && (
                    <p className="text-xs text-amber-700 font-semibold">
                      ⚠︎ El ancho ingresado supera la tabla. Usamos la tarifa de {previewEstimate.widthBucket}" hasta confirmar medidas.
                    </p>
                  )}
                  {previewEstimate?.price ? (
                    <div className="rounded-2xl border border-emerald-200 bg-white/70 px-4 py-3 text-sm text-emerald-900 flex flex-col gap-1">
                      <div className="flex justify-between">
                        <span>Precio unitario estimado</span>
                        <strong>${previewEstimate.price.toFixed(2)}</strong>
                      </div>
                      <div className="flex justify-between text-emerald-800/80">
                        <span>Subtotal x {model.quantity || 1}</span>
                        <strong>${(previewEstimate.price * (Number(model.quantity) || 1)).toFixed(2)}</strong>
                      </div>
                    </div>
                  ) : null}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Serie, cristal y acabados se definirán en la visita técnica.</span>
                    <button type="button" onClick={() => handleModelRemove(index)} className="text-red-600 font-semibold">
                      Eliminar
                    </button>
                  </div>
                </article>
                );
              })}
            </div>
          ) : (
            <div className="rounded-3xl border border-emerald-100 bg-emerald-50/60 shadow-sm flex flex-col md:flex-row">
              {selectedDesign.image ? (
                <img
                  src={selectedDesign.image}
                  alt={selectedDesign.title}
                  className="h-36 w-full md:w-48 object-cover rounded-t-3xl md:rounded-l-3xl md:rounded-tr-none"
                />
              ) : null}
              <div className="flex-1 p-4 space-y-2">
                <h3 className="text-lg font-semibold text-gray-900">{selectedDesign.title}</h3>
                <p className="text-sm text-gray-600">
                  {selectedDesign.desc || 'Estilo seleccionado para esta cotización.'}
                </p>
                <p className="text-xs text-gray-500">Las imágenes y especificaciones se validan en la visita técnica.</p>
              </div>
            </div>
          )}
        </section>

        {!models.length && (
          <>
            <section className="space-y-4">
              <p className="text-xs uppercase tracking-[0.4em] text-gray-500">Selecciona el producto</p>
              <div className="grid md:grid-cols-3 gap-4">
                {catalogProductCategories.map((category) => {
                  const isActive = formData.productCategory === category.id;
                  return (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => handleProductCategory(category.id)}
                      className={`text-left rounded-2xl border p-4 transition ${
                        isActive ? 'border-emerald-500 bg-emerald-50 shadow-md' : 'border-gray-200 bg-white'
                      }`}
                    >
                      <p className="text-sm font-semibold text-gray-900">{category.title}</p>
                      <p className="text-xs text-gray-500 mt-1">{category.desc}</p>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="space-y-4">
              <div className="flex flex-col gap-2">
                <p className="text-xs uppercase tracking-[0.4em] text-gray-500">Estilos disponibles</p>
                <p className="text-sm text-gray-600">Selecciona un diseño para comenzar la cotización.</p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {currentDesigns.map((design) => {
                  const isSelected = formData.designStyle === design.id;
                  return (
                    <article
                      key={design.id}
                      className={`rounded-3xl border overflow-hidden shadow-sm transition cursor-pointer ${
                        isSelected ? 'border-emerald-500 shadow-lg' : 'border-gray-200'
                      }`}
                      onClick={() => handleDesignSelect(design.id)}
                    >
                      <img src={design.image} alt={design.title} className="h-36 w-full object-cover" />
                      <div className="p-4 space-y-1">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-semibold text-gray-900">{design.title}</h3>
                          {isSelected && (
                            <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full">
                              Seleccionado
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{design.desc}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>

            <section className="space-y-4">
              {formData.productCategory === 'ventanas' && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-700">Tipo de lama</label>
                    <select
                      name="windowSeries"
                      value={formData.windowSeries}
                      onChange={handleInputChange}
                      className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      {windowSeriesOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    Ajusta el número de lamas según el modelo de ventana. Las tablas de lama 3, 4 y 6 alimentan el precio base.
                  </div>
                </div>
              )}

              <p className="text-xs uppercase tracking-[0.4em] text-gray-500">Medidas y cantidades</p>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700">Alto (pulgadas)</label>
                  <input
                    type="number"
                    name="height"
                    value={formData.height}
                    onChange={handleInputChange}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Ej. 72"
                    min="1"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">Ancho (pulgadas)</label>
                  <input
                    type="number"
                    name="width"
                    value={formData.width}
                    onChange={handleInputChange}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Ej. 48"
                    min="1"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">Cantidad</label>
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    min="1"
                    required
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700">Color del marco</label>
                  <select
                    name="color"
                    value={formData.color}
                    onChange={handleInputChange}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    {catalogColorOptions.map((color) => (
                      <option key={color.id} value={color.id}>
                        {color.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">Cristal</label>
                  <select
                    name="glass"
                    value={formData.glass}
                    onChange={handleInputChange}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    {catalogGlassOptions.map((glass) => (
                      <option key={glass.id} value={glass.id}>
                        {glass.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">Acabado</label>
                  <select
                    name="finish"
                    value={formData.finish}
                    onChange={handleInputChange}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    {catalogFinishOptions.map((finish) => (
                      <option key={finish.id} value={finish.id}>
                        {finish.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                {measurementTips.map((tip) => (
                  <div key={tip.title} className="rounded-2xl border border-emerald-100 bg-white p-4 shadow-sm">
                    <p className="text-sm font-semibold text-gray-900">{tip.title}</p>
                    <p className="text-xs text-gray-600 mt-1">{tip.desc}</p>
                  </div>
                ))}
              </div>

              {formData.productCategory === 'ventanas' && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 space-y-1">
                  <p className="text-sm font-semibold text-emerald-800">Estimado base ventana</p>
                  {windowEstimate ? (
                    <>
                      <p className="text-2xl font-bold text-emerald-900">
                        ${windowEstimate.price.toFixed(2)}{' '}
                        <span className="text-sm font-normal text-emerald-700">por unidad estimada</span>
                      </p>
                      <p className="text-xs text-emerald-800">
                        Referencia: ancho ≤ {windowEstimate.widthBucket}"{windowEstimate.widthExceeded ? ' (se usó el máximo disponible en tabla)' : ''} · altura aprox. {windowEstimate.heightReference}" ·{' '}
                        {windowEstimate.lamas} lamas · Serie {formatSeriesLabel(windowEstimate.series)}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-emerald-800">Ingresa alto y ancho para calcular el rango de precio.</p>
                  )}
                  <p className="text-xs text-emerald-700">
                    Basado en la tabla "Ventana regular {formatSeriesLabel(windowEstimate?.series) || 'Lama'}". El precio final se ajusta según cristal, color y accesorios.
                  </p>
                </div>
              )}
            </section>
          </>
        )}

        <section className="space-y-4">
          <div className="flex flex-col gap-2">
            <p className="text-xs uppercase tracking-[0.4em] text-gray-500">Accesorios y servicios</p>
            <p className="text-sm text-gray-600">Asigna cantidades a los accesorios que deseas incluir.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4 max-h-[360px] overflow-y-auto pr-1">
            {filteredAccessories.map((item) => {
              const quantity = formData.accessoryQuantities[item.id] || 0;
              return (
                <div
                  key={item.id}
                  className="rounded-2xl border border-gray-200 p-4 shadow-sm bg-white flex flex-col gap-3"
                >
                  <div>
                    <h4 className="text-base font-semibold text-gray-900">{item.name}</h4>
                    <p className="text-xs text-gray-500">{item.description}</p>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-700">
                    <span>
                      ${item.price.toFixed(2)} <span className="text-gray-400">/ {item.unit}</span>
                    </span>
                    <div className="flex items-center gap-2">
                      <label className="text-xs uppercase tracking-wide text-gray-500">Cantidad</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={quantity}
                        onChange={(e) => handleAccessoryQuantityChange(item.id, e.target.value)}
                        className="w-20 rounded-2xl border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {selectedAccessories.length > 0 && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-sm font-semibold text-emerald-900 mb-2">Resumen de accesorios</p>
              <ul className="space-y-1 text-sm text-emerald-800">
                {selectedAccessories.map((item) => (
                  <li key={item.id} className="flex justify-between">
                    <span>
                      {item.quantity} × {item.name}
                    </span>
                    <span>${(item.quantity * item.price).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              <div className="border-t border-emerald-200 mt-2 pt-2 flex justify-between text-sm font-semibold text-emerald-900">
                <span>Total estimado accesorios</span>
                <span>${accessoriesTotal.toFixed(2)}</span>
              </div>
            </div>
          )}
        </section>

        <section className="space-y-4">
          <p className="text-xs uppercase tracking-[0.4em] text-gray-500">Certificaciones y notas</p>
          <div className="flex flex-wrap gap-3">
            {certificationOptions.map((cert) => {
              const checked = formData.certifications.includes(cert);
              return (
                <button
                  key={cert}
                  type="button"
                  onClick={() => toggleCertification(cert)}
                  className={`rounded-full border px-4 py-1 text-sm font-semibold transition ${
                    checked ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-gray-200 text-gray-600'
                  }`}
                >
                  {cert}
                </button>
              );
            })}
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-700">Notas adicionales</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={4}
              className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="Ubicación exacta, niveles de impacto deseados, fechas tentativas..."
            />
          </div>
        </section>

        <div>
          <button
            type="submit"
            className="w-full py-3 rounded-2xl text-white font-semibold shadow-lg transition disabled:opacity-60"
            style={{ backgroundImage: 'linear-gradient(120deg, #0f5132, #0b3d24)' }}
            disabled={!formValid || submitting}
          >
            {submitting ? 'Enviando cotización...' : 'Generar cotización preliminar'}
          </button>
          {message && <p className="text-sm text-center mt-3 text-emerald-700">{message}</p>}
        </div>
      </form>
    </div>
  );
};

export default BusinessInfoForm;
const generatePreliminaryQuotePDF = ({
  formData,
  models = [],
  accessories,
  accessoriesTotal,
  leadId,
  categoryOptions = defaultProductCategories,
}) => {
  if (typeof window === 'undefined') return;
  const normalizedModels = models.length
    ? models
    : [
        {
          name: formData.designLabel || 'Modelo seleccionado',
          description: formData.designDesc || '',
          image: formData.designImage || '',
          categoryName: resolveCategoryTitle(formData.productCategory, categoryOptions),
          width: Number(formData.width) || null,
          height: Number(formData.height) || null,
          quantity: Number(formData.quantity) || 1,
          series: formData.productCategory === 'ventanas' ? formData.windowSeries : null,
          estimate: null,
        },
      ];
  const baseTotal = normalizedModels.reduce((sum, model) => {
    const unit = model.estimate?.price || 0;
    return sum + unit * (Number(model.quantity) || 1);
  }, 0);
  const grandTotal = baseTotal + accessoriesTotal;
  const quoteNumber = leadId ? `PRWD-${leadId}` : `PRWD-${Date.now()}`;
  const primaryModel = normalizedModels[0];
  const selectedDesignName = primaryModel?.name || formData.designLabel || formData.designStyle || 'Modelo seleccionado';
  const selectedDesignDesc = primaryModel?.description || formData.designDesc || '';
  const selectedDesignImage = primaryModel?.image || formData.designImage || '';

  const accessoriesRows = accessories.length
    ? accessories
        .map(
          (item) => `
            <tr>
              <td>${item.name}</td>
              <td>${item.quantity}</td>
              <td>$${item.price.toFixed(2)}</td>
              <td>$${(item.price * item.quantity).toFixed(2)}</td>
            </tr>`
        )
        .join('')
    : `<tr><td colspan="4">Sin accesorios seleccionados.</td></tr>`;

  const summaryRows = `
    ${baseTotal
      ? `<tr><td>Subtotal base</td><td>$${baseTotal.toFixed(2)}</td></tr>`
      : '<tr><td>Subtotal base</td><td>Por definir en visita técnica</td></tr>'}
    <tr><td>Total accesorios</td><td>$${accessoriesTotal.toFixed(2)}</td></tr>
    <tr><td><strong>Estimado preliminar</strong></td><td><strong>$${grandTotal.toFixed(2)}</strong></td></tr>
  `;
  const modelsTableRows = normalizedModels
    .map((model) => {
      const dims = model.height && model.width ? `${model.height}" × ${model.width}"` : 'Pendiente';
      const qty = Number(model.quantity) || 1;
      const seriesLabel = model.series ? formatSeriesLabel(model.series) : '—';
      const unit = model.estimate?.price ? `$${model.estimate.price.toFixed(2)}` : 'Pendiente';
      const subtotal =
        model.estimate?.price && qty
          ? `$${(model.estimate.price * qty).toFixed(2)}`
          : 'Por definir';
      const widthNote = model.estimate?.widthExceeded ? ' (usando máximo de tabla)' : '';
      const reference = model.estimate
        ? `Ancho ≤ ${model.estimate.widthBucket}"${widthNote} · Altura ${model.estimate.heightReference}" · ${model.estimate.lamas} lamas`
        : 'Validaremos medidas durante la visita técnica.';

      return `
        <tr>
          <td>
            <strong>${model.name || 'Modelo'}</strong><br />
            <span style="color:#6b9080; font-size:11px;">${model.categoryName || ''}</span><br />
            <span style="color:#9aaea2; font-size:11px;">${reference}</span>
          </td>
          <td>${dims}</td>
          <td>${qty}</td>
          <td>${seriesLabel}</td>
          <td>${unit}</td>
          <td>${subtotal}</td>
        </tr>
      `;
    })
    .join('');

  const certificationsText = formData.certifications.length
    ? formData.certifications.join(', ')
    : 'No especificado';

  const htmlContent = `
    <div style="font-family: 'Helvetica', Arial, sans-serif; color:#0b3d24; padding:24px; max-width:800px;">
      <header style="text-align:center; border-bottom:2px solid #0f5132; padding-bottom:16px; margin-bottom:24px;">
        <h1 style="margin:0; font-size:24px;">Puerto Rico Windows & Doors</h1>
        <p style="margin:4px 0; font-size:13px;">WhatsApp: +1 (787) 236-9189 · info@puertoricowindows.com</p>
        <h2 style="margin:8px 0 0; font-size:20px;">Cotización preliminar</h2>
        <p style="margin:4px 0; font-size:12px; letter-spacing:0.2em;">${quoteNumber}</p>
      </header>

      <section style="margin-bottom:24px;">
        <h3 style="font-size:16px; margin-bottom:8px;">Datos del cliente</h3>
        <table style="width:100%; border-collapse:collapse; font-size:13px;">
          <tr>
            <td><strong>Nombre:</strong> ${formData.fullName || '-'}</td>
            <td><strong>Email:</strong> ${formData.email || '-'}</td>
          </tr>
          <tr>
            <td><strong>Teléfono:</strong> ${formData.phone || '-'}</td>
            <td><strong>Ubicación:</strong> ${formData.projectLocation || '-'}</td>
          </tr>
          <tr>
            <td><strong>Uso del proyecto:</strong> ${formData.projectType}</td>
            <td><strong>Fecha:</strong> ${new Date().toLocaleDateString()}</td>
          </tr>
        </table>
      </section>

      <section style="margin-bottom:24px;">
        <h3 style="font-size:16px; margin-bottom:8px;">Resumen del proyecto</h3>
        <table style="width:100%; border-collapse:collapse; font-size:13px;">
          <tr>
            <td><strong>Tipo principal:</strong> ${primaryModel?.categoryName || resolveCategoryTitle(formData.productCategory, categoryOptions)}</td>
            <td><strong>Modelo destacado:</strong> ${selectedDesignName}</td>
          </tr>
          <tr>
            <td><strong>Color / Cristal / Acabado:</strong> ${formData.color} / ${formData.glass} / ${formData.finish}</td>
            <td><strong>Uso:</strong> ${formData.projectType}</td>
          </tr>
          <tr>
            <td colspan="2"><strong>Certificaciones:</strong> ${certificationsText}</td>
          </tr>
          <tr>
            <td colspan="2"><strong>Notas:</strong> ${formData.notes || 'Sin notas adicionales.'}</td>
          </tr>
        </table>
     </section>

      <section style="margin-bottom:24px;">
        <h3 style="font-size:16px; margin-bottom:8px;">Modelos seleccionados</h3>
        <div style="display:flex; gap:16px; border:1px solid #d4e6d2; border-radius:16px; overflow:hidden;">
          ${selectedDesignImage
            ? `<img src="${selectedDesignImage}" alt="${selectedDesignName}" style="width:220px; object-fit:cover;" />`
            : ''}
          <div style="padding:16px;">
            <p style="font-size:14px; font-weight:600; color:#0b3d24;">${selectedDesignName}</p>
            <p style="font-size:12px; color:#4a5f58;">${selectedDesignDesc || 'Estilo elegido para esta cotización preliminar.'}</p>
          </div>
        </div>
        <table style="width:100%; border-collapse:collapse; font-size:13px; margin-top:16px;">
          <thead>
            <tr style="background:#eaf4ee;">
              <th style="padding:6px; text-align:left;">Modelo</th>
              <th style="padding:6px; text-align:left;">Medidas</th>
              <th style="padding:6px; text-align:center;">Cantidad</th>
              <th style="padding:6px; text-align:center;">Serie</th>
              <th style="padding:6px; text-align:right;">Precio unitario</th>
              <th style="padding:6px; text-align:right;">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            ${modelsTableRows}
          </tbody>
        </table>
      </section>

      <section style="margin-bottom:24px;">
        <h3 style="font-size:16px; margin-bottom:8px;">Accesorios seleccionados</h3>
        <table style="width:100%; border-collapse:collapse; font-size:13px;">
          <thead>
            <tr style="background:#eaf4ee;">
              <th style="padding:6px; text-align:left;">Accesorio</th>
              <th style="padding:6px; text-align:right;">Cantidad</th>
              <th style="padding:6px; text-align:right;">Precio</th>
              <th style="padding:6px; text-align:right;">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            ${accessoriesRows}
          </tbody>
        </table>
      </section>

      <section style="margin-bottom:24px;">
        <h3 style="font-size:16px; margin-bottom:8px;">Totales preliminares</h3>
        <table style="width:100%; border-collapse:collapse; font-size:13px;">
          ${summaryRows}
        </table>
        <p style="font-size:12px; margin-top:8px; color:#555;">
          * Estos montos son estimados y están sujetos a verificación de medidas, disponibilidad de materiales y revisión técnica.
        </p>
      </section>

      <section style="margin-bottom:24px;">
        <h3 style="font-size:16px; margin-bottom:8px;">Siguientes pasos</h3>
        <ol style="font-size:13px; padding-left:20px; color:#333;">
          <li>Un asesor te contactará en menos de 24 horas.</li>
          <li>Coordinamos visita técnica para validar medidas y requerimientos.</li>
          <li>Recibes la cotización final y cronograma de instalación.</li>
          <li>Confirmas tu orden y agendamos instalación.</li>
        </ol>
      </section>

      <footer style="border-top:1px solid #c5d5c0; padding-top:12px; font-size:11px; color:#4b6353;">
        Puerto Rico Windows & Doors · Servicio rápido y garantizado · puertoricowindows.com
      </footer>
    </div>
  `;

  const element = document.createElement('div');
  element.innerHTML = htmlContent;

  html2pdf()
    .set({
      margin: 10,
      filename: `${quoteNumber}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    })
    .from(element)
    .save()
    .finally(() => {
      element.remove();
    });
};
